import { catchError, filter, map, of } from 'rxjs';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Ripple } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { MainLayoutComponent, TimeAgoPipe } from '@adlatus-gui/shared';
import {
  ClearingTicketCustomService,
  ClearingTicketService
} from '@adlatus-gui/domain/clearing';
import {
  ITU_CODE,
  LightPartnerService,
  Notification,
  OWNER_SUBENTITIES,
} from '@adlatus-gui/domain/clearing-custom';
import { ClearingTicketUtils } from './core/util/clearingTicket.util';
import { environment } from '../environments/environment';
import { MenuItem, PrimeNGConfig } from 'primeng/api';
import { TicketOriginatorUtil } from './core/util/ticketOriginatorUtil';
import { TicketCategoryEnum } from '@adlatus-gui/domain/clearing-custom';
import { BadgeModule } from 'primeng/badge';
import { MenuModule } from 'primeng/menu';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { WebSocketService } from './core/service/WebSocketService';
import { AuthenticationService, roles } from '@adlatus-gui/domain/authentication';
import { TicketRefreshService } from './core/service/TicketRefreshService';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OwnerScopeService } from './core/service/owner-scope.service';

import enTranslation from '../../../../libs/shared/src/lib/assets/i18n/en.json';
import deTranslation from '../../../../libs/shared/src/lib/assets/i18n/de.json';

@Component({
  standalone: true,
  imports: [
    MainLayoutComponent,
    Ripple,
    RouterLink,
    BadgeModule,
    MenuModule,
    OverlayPanelModule,
    TooltipModule,
    StyleClassModule,
    NgIf,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  providers: [
    LightPartnerService,
    ClearingTicketUtils,
    ClearingTicketService,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  readonly partnershipMgmtUrl: string = environment.PARTNERSHIP_APP_URL;
  private readonly _timeAgoPipe: any;

  @ViewChild('notificationsPanel') notificationsPanel!: OverlayPanel;

  title = 'clearing-app';
  totalInbox: number | null = 0;
  totalOutbox: number | null = 0;
  totalMyIssues = '0';

  totalWatchingIssues = '0';
  canManageSettings = true;
  canSeeMigration = false;
  settingsExpanded = false;

  notificationsMenuItems: MenuItem[] = [];
  private countsSubscribed = false;
  private ownersCompanies: string[] = [];
  private ownerScopeResolved = false;

  constructor(
    private router: Router,
    @Inject(ITU_CODE) ituCode: string[],
    @Inject(OWNER_SUBENTITIES) ownerSubEntities: string[],
    private primengConfig: PrimeNGConfig,
    private translateService: TranslateService,
    private lightPartnerService: LightPartnerService,
    private ticketRefreshService: TicketRefreshService,
    private authenticationService: AuthenticationService,
    private webSocketService: WebSocketService,
    private clearingTicketUtils: ClearingTicketUtils,
    private ticketOriginatorUtil: TicketOriginatorUtil,
    private clearingTicketCustomService: ClearingTicketCustomService,
    private ownerScopeService: OwnerScopeService
  ) {
    this.ownersCompanies = [...ituCode, ...ownerSubEntities];
    this._timeAgoPipe = new TimeAgoPipe(this.translateService);

    this.translateService.setTranslation('en', enTranslation);
    this.translateService.setTranslation('de', deTranslation);
    this.translateService.setDefaultLang('de');

    const selectedLang = localStorage.getItem('selectedLanguage');
    if (selectedLang) {
      this.translateService.use(selectedLang);
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.ownerScopeResolved) {
          this.fetchTicketsCount();
        }
      });

    this.subscribeToNotifications();
    this.fetchNotifications();
    this.loadCalendarLocale();
    this.resolveSettingsAccess();
    this.translateService.onLangChange.subscribe(() => {
      this.loadCalendarLocale();
      this.subscribeToNotifications();
      this.fetchNotifications();
    });
  }

  ngOnInit(): void {
    this.resolveMigrationAccess();
  }

  private resolveMigrationAccess() {
    this.canSeeMigration = this.authenticationService
      .getUserRoles()
      .includes(roles.CLEARING_SUPER_ADMINISTRATOR);
  }

  private resolveSettingsAccess() {
    this.ownerScopeService.getOwnerScope().subscribe({
      next: (ownerScope) => {
        const platformOwner = ownerScope?.platformOwnerCode;
        const ownerCodes = ownerScope?.ownerCodes ?? [];
        if (ownerCodes.length) {
          this.ownersCompanies = ownerCodes;
        }
        this.ownerScopeResolved = true;
        this.canManageSettings =
          !(
            ownerScope?.mode === 'PROXY' &&
            ownerCodes.length === 1 &&
            !!platformOwner &&
            ownerCodes[0] !== platformOwner
          );
        this.fetchTicketsCount();
      },
      error: () => {
        this.ownerScopeResolved = true;
        this.canManageSettings = true;
        this.fetchTicketsCount();
      },
    });
  }

  loadCalendarLocale() {
    const calendarTranslations = this.translateService.instant('calendar');
    this.primengConfig.setTranslation({
      ...calendarTranslations,
    });
  }

  toggleSettingsMenu() {
    this.settingsExpanded = !this.settingsExpanded;
  }

  private fetchNotifications() {
    this.clearingTicketCustomService.listMyNotifications().subscribe({
      next: (notifications) =>
        this.handleListMyNotificationsResponse(notifications),
    });
  }

  private subscribeToNotifications() {
    if (this.authenticationService.isLoggedIn()) {
      this.authenticationService.getUserProfile().then((userProfile) => {
        this.webSocketService.subscribe(
          `/topic/${userProfile.id}/notifications`,
          (messageBody) => {
            const notification: Notification = JSON.parse(messageBody);
            const notifyMenuItem = this.buildNotification(notification);
            this.notificationsMenuItems = [
              notifyMenuItem,
              ...this.notificationsMenuItems.map((notify) =>
                this.buildNotification(notify['data'])
              ),
            ];
          }
        );
      });
    }
  }

  private fetchTicketsCount() {
    const url = this.router.url;
    if (!url.toLowerCase().includes('light')) {
      this.fetchDefaultVisibleTicketCount({
        inbox: true,
      }).subscribe((count) => {
        this.totalInbox = count;
        this.clearingTicketUtils.updateInboxCount(count);
      });

      this.fetchDefaultVisibleTicketCount({
        outbox: true,
      }).subscribe((count) => {
        this.totalOutbox = count;
        this.clearingTicketUtils.updateOutboxCount(count);
      });

      this.fetchDefaultVisibleTicketCount({
        myIssues: true,
      }).subscribe((count) => {
        const countAsString = count.toString();
        this.totalMyIssues = countAsString;
        this.clearingTicketUtils.updateMyIssuesCount(countAsString);
      });

      this.fetchDefaultVisibleTicketCount({
        myWatchingIssues: true,
      }).subscribe((count) => {
        const countAsString = count.toString();
        this.totalWatchingIssues = countAsString;
        this.clearingTicketUtils.updateWatchingIssues(countAsString);
      });

      if (!this.countsSubscribed) {
        this.clearingTicketUtils.totalInbox$.subscribe(
          (count) => (this.totalInbox = count)
        );
        this.clearingTicketUtils.totalOutbox$.subscribe(
          (count) => (this.totalOutbox = count)
        );
        this.clearingTicketUtils.totalMyIssues$.subscribe(
          (count) => (this.totalMyIssues = count)
        );
        this.clearingTicketUtils.totalWatchingIssues$.subscribe(
          (count) => (this.totalWatchingIssues = count)
        );
        this.countsSubscribed = true;
      }
    }
  }

  private fetchDefaultVisibleTicketCount(filters: {
    inbox?: boolean;
    outbox?: boolean;
    myIssues?: boolean;
    myWatchingIssues?: boolean;
  }) {
    const searchQuery = this.buildOwnershipSearchQuery(filters);
    return this.clearingTicketCustomService
      .listClearingTicket(
        searchQuery,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        filters.myIssues,
        filters.myWatchingIssues,
        filters.inbox,
        filters.outbox,
        false,
        false,
        0,
        1,
        'response'
      )
      .pipe(
        map((response) => Number(response.headers.get('X-Total-Count') ?? 0)),
        catchError(() => of(0))
      );
  }

  private buildOwnershipSearchQuery(filters: {
    inbox?: boolean;
    outbox?: boolean;
  }): string | undefined {
    if (this.ownersCompanies.length === 0) {
      return undefined;
    }

    if (filters.inbox) {
      const processorConditions = this.ownersCompanies
        .map((company) => `processor:${company}`)
        .join(' OR ');
      return `(${processorConditions})`;
    }

    if (filters.outbox) {
      const originatorConditions = this.ownersCompanies
        .map((company) => `originator:${company}`)
        .join(' OR ');
      return `(${originatorConditions})`;
    }

    return undefined;
  }

  private handleListMyNotificationsResponse(notifications: Notification[]) {
    if (notifications && notifications.length) {
      this.notificationsMenuItems = notifications
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map(
        (notification: Notification) => {
          return this.buildNotification(notification);
        }
      );
    }
  }

  private buildNotification(notification: Notification) {
    let translatedText = notification.text;
    if (notification.text.includes('Severity has been changed to:')) {
      const severity = notification.text.split(': ')[1];
      const severityTranslation = this.translateService.instant(
        severity.toLowerCase()
      );
      translatedText = this.translateService
        .instant('notification.severity_changed')
        .replace('{0}', severityTranslation);
    } else if (notification.text.includes('Status has been changed to:')) {
      const status = notification.text.split(': ')[1];
      const statusTranslation = this.translateService.instant(status.trim());
      translatedText = this.translateService
        .instant('notification.status_changed')
        .replace('{0}', statusTranslation);
    } else if (notification.text === 'Ticket created') {
      translatedText = this.translateService.instant(
        'notification.ticket_created'
      );
    } else if (notification.text === 'Data changed') {
      translatedText = this.translateService.instant(
        'notification.data_changed'
      );
    } else if (notification.text === 'Note added') {
      translatedText = this.translateService.instant('notification.note_added');
    } else if (notification.text === 'Ticket resolved') {
      translatedText = this.translateService.instant(
        'notification.ticket_resolved'
      );
    }

    return {
      label: `<div class='m-1' style='position: relative;'><b>Clearing Ticket:</b> ${
        notification.clearingTicket.id
      }<p> ${translatedText}</p><small>${this._timeAgoPipe.transform(
        notification.createdAt
      )}</small></div><div style='margin-top: 20px; width: 18vw; border-bottom: 1px solid rgba(0, 0, 0, 0.1); position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);'></div>`,
      style: { width: '100%' },
      command: () => this.handleNotificationClick(notification),
      escape: false,
      data: notification,
    };
  }

  private handleNotificationClick(notification: Notification) {
    if (notification && notification.clearingTicket.id) {
      const ticketType = this.ticketOriginatorUtil.isOriginatorPlatform(
        notification.clearingTicket.originator
      )
        ? TicketCategoryEnum.OUTBOX
        : TicketCategoryEnum.INBOX;
      this.clearingTicketCustomService
        .readMyNotificationsByTicketId(notification.clearingTicket.id)
        .subscribe({
          next: () => {
            this.notificationsPanel.hide();
            this.notificationsMenuItems = this.notificationsMenuItems.filter(
              (item) =>
                (<Notification>item['data']).clearingTicket.id !==
                notification.clearingTicket.id
            );
          },
        });
      if (
        this.router.url ===
        `/tickets/${ticketType}/${notification.clearingTicket.id}`
      ) {
        this.ticketRefreshService.triggerTicketRefresh(true);
      } else {
        this.router
          .navigate(['tickets', ticketType, notification.clearingTicket.id])
          .then();
      }
    }
  }

  get createTicketRoute() {
    return this.lightPartnerService.getIsLightPartner()
      ? ['light', 'create-ticket']
      : ['create-ticket'];
  }

  get templatesRoute() {
    return this.lightPartnerService.getIsLightPartner()
      ? ['light', 'templates']
      : ['templates'];
  }

  markAllUserSentNotificationsAsRead() {
    this.notificationsPanel.hide();
    this.notificationsMenuItems = [];
    this.clearingTicketCustomService
      .markAllUserSentNotificationsAsRead()
      .subscribe();
  }

  ngOnDestroy(): void {
    this.webSocketService.disconnect();
  }
}
